/**
 * What woke an agent, as a label and a place to go.
 *
 * `trigger.kind` is one of the BE's inbox kinds. The label is a catalog key
 * plus interpolation values (never a pre-built English string), and the
 * link is a plain description of the destination so the caller decides how
 * to navigate and the mapping stays testable without a router.
 */

import type { AgentRunTrigger } from '@/api/types-bridge'

export type RunTriggerLink =
  | { type: 'conversation'; conversationId: string; messageId: string | null }
  | { type: 'card'; cardId: string; projectId: string | null; commentId: string | null }
  | { type: 'wikiDoc'; docId: string; threadId: string | null }

/** The keys under `agents:runs.trigger.`. */
export type RunTriggerLabelKey =
  | 'chatDm'
  | 'chatDmAnon'
  | 'chatMention'
  | 'chatMentionAnon'
  | 'chatThreadReply'
  | 'chatThreadReplyAnon'
  | 'cardAssigned'
  | 'cardUnassigned'
  | 'cardMention'
  | 'cardComment'
  | 'cardCommentReply'
  | 'cardAssignedRef'
  | 'cardUnassignedRef'
  | 'cardMentionRef'
  | 'cardCommentRef'
  | 'cardCommentReplyRef'
  | 'wikiMention'
  | 'wikiReply'
  | 'wikiMentionDoc'
  | 'wikiReplyDoc'
  | 'followup'
  | 'budgetReset'
  | 'unknown'

export interface RunTriggerLabel {
  key: RunTriggerLabelKey
  /**
   * `actor` is only present when the key reads "from {actor}"; `ref` is the
   * card shorthand and `doc` the wiki doc title for the `*Ref` / `*Doc` keys.
   */
  values: { actor?: string; kind?: string; ref?: string; doc?: string }
  /** The trailing free-text note a follow-up carries, if any. */
  note: string | null
}

const CARD_KINDS = new Set(['task', 'ticket', 'event', 'announcement'])

/** Normalises a card kind for the ICU `select` in the catalog; anything else is `other`. */
export const cardKindForLabel = (kind: string | undefined): string =>
  kind && CARD_KINDS.has(kind) ? kind : 'other'

/** Card kind → [generic label, label that names the card by its shorthand]. */
const CARD_KEYS: Readonly<Record<string, readonly [RunTriggerLabelKey, RunTriggerLabelKey]>> = {
  'card.assigned': ['cardAssigned', 'cardAssignedRef'],
  'card.unassigned': ['cardUnassigned', 'cardUnassignedRef'],
  'card.mention': ['cardMention', 'cardMentionRef'],
  'card.comment': ['cardComment', 'cardCommentRef'],
  'card.comment_reply': ['cardCommentReply', 'cardCommentReplyRef'],
}

export function describeRunTrigger(
  trigger: AgentRunTrigger,
  /** The actor's display name, when the caller could resolve one. */
  actorName: string | null,
): RunTriggerLabel {
  const { kind, source } = trigger
  const note = source.note?.trim() || null
  // Display-only strings the BE resolves onto the source; absent on older runs.
  const cardRef = source.cardShorthand?.trim() || null
  const docTitle = source.docTitle?.trim() || null

  const named = (withActor: RunTriggerLabelKey, without: RunTriggerLabelKey): RunTriggerLabel =>
    actorName
      ? { key: withActor, values: { actor: actorName }, note }
      : { key: without, values: {}, note }

  switch (kind) {
    case 'chat.dm':
      return named('chatDm', 'chatDmAnon')
    case 'chat.mention':
      return named('chatMention', 'chatMentionAnon')
    case 'chat.thread_reply':
      return named('chatThreadReply', 'chatThreadReplyAnon')
    case 'wiki_comment.mention':
      return docTitle
        ? { key: 'wikiMentionDoc', values: { doc: docTitle }, note }
        : { key: 'wikiMention', values: {}, note }
    case 'wiki_comment.reply':
      return docTitle
        ? { key: 'wikiReplyDoc', values: { doc: docTitle }, note }
        : { key: 'wikiReply', values: {}, note }
    case 'followup':
      return { key: 'followup', values: {}, note }
    case 'budget.reset':
      return { key: 'budgetReset', values: {}, note }
    default: {
      const cardKeys = CARD_KEYS[kind]
      if (cardKeys) {
        return cardRef
          ? { key: cardKeys[1], values: { ref: cardRef }, note }
          : { key: cardKeys[0], values: { kind: cardKindForLabel(source.cardKind) }, note }
      }
      return { key: 'unknown', values: {}, note }
    }
  }
}

/**
 * Where a run's trigger points, or null when the source carries no usable
 * target. Driven by which ids the source carries rather than by kind, so a
 * follow-up the agent scheduled from a conversation or a card links too.
 */
export function resolveRunTriggerLink(trigger: AgentRunTrigger): RunTriggerLink | null {
  const { source } = trigger
  if (source.conversationId) {
    return {
      type: 'conversation',
      conversationId: source.conversationId,
      messageId: source.messageId ?? null,
    }
  }
  if (source.cardId) {
    return {
      type: 'card',
      cardId: source.cardId,
      projectId: source.projectId ?? null,
      commentId: source.commentId ?? null,
    }
  }
  if (source.docId) {
    return { type: 'wikiDoc', docId: source.docId, threadId: source.threadId ?? null }
  }
  return null
}
